/**
 * One asset's sign-out history (GET /equipment/:id/history, api#985) and its
 * last job, turned into what the two detail screens print.
 *
 * Two steps, kept apart so the real-payload test can pin each:
 *
 *   parseEquipmentHistory  the wire → typed rows. Reads both spellings
 *                          (`jobNumber ?? job_number`). A payload whose `data`
 *                          is not an array THROWS: that is a failed load, and
 *                          turning it into `[]` would print "No history yet."
 *                          over a response nobody could read.
 *   describeHistoryRow     a row → the words on the screen. Nothing is
 *                          recomputed: `atLocal` is printed as the API sent it,
 *                          already in the tenant's zone.
 *
 * Absent renders `—`, per CLAUDE.md. An actor the API could not resolve inside
 * the tenant (id kept, both names null) is "Unknown user", not `—` — the API
 * says somebody did it, and `—` would read as "nobody recorded".
 */
import { equipmentStatusLabel } from './equipment-status';
import { formatInstantTenantDay } from './calendar-date';
import type {
  EquipmentHistoryActor,
  EquipmentHistoryChange,
  EquipmentHistoryResponse,
  EquipmentHistoryRow,
  EquipmentLastJob,
} from '../types/equipment-history';

const DASH = '—';

function str(v: unknown): string | null {
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? null : t;
  }
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function pick(raw: any, camel: string, snake: string): unknown {
  return raw?.[camel] ?? raw?.[snake];
}

function parseActor(raw: any): EquipmentHistoryActor | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = str(raw.id);
  if (!id) return null;
  return {
    id,
    firstName: str(pick(raw, 'firstName', 'first_name')),
    lastName: str(pick(raw, 'lastName', 'last_name')),
  };
}

function parseChanges(raw: unknown): EquipmentHistoryChange[] | null {
  if (!Array.isArray(raw)) return null;
  return raw
    .filter((c) => c && typeof c === 'object' && str((c as any).field))
    .map((c: any) => ({ field: str(c.field) as string, before: c.before ?? null, after: c.after ?? null }));
}

export function parseHistoryRow(raw: any): EquipmentHistoryRow {
  return {
    id: str(raw?.id) ?? '',
    source: str(raw?.source),
    type: str(raw?.type) ?? '',
    fromStatus: str(pick(raw, 'fromStatus', 'from_status')),
    toStatus: str(pick(raw, 'toStatus', 'to_status')),
    assignedSource: str(pick(raw, 'assignedSource', 'assigned_source')),
    jobId: str(pick(raw, 'jobId', 'job_id')),
    jobNumber: str(pick(raw, 'jobNumber', 'job_number')),
    rigName: str(pick(raw, 'rigName', 'rig_name')),
    wellName: str(pick(raw, 'wellName', 'well_name')),
    fromJobId: str(pick(raw, 'fromJobId', 'from_job_id')),
    fromJobNumber: str(pick(raw, 'fromJobNumber', 'from_job_number')),
    actor: parseActor(raw?.actor),
    changedFields: parseChanges(pick(raw, 'changedFields', 'changed_fields')),
    reason: str(raw?.reason),
    note: str(raw?.note),
    detail: raw?.detail && typeof raw.detail === 'object' ? raw.detail : null,
    at: str(raw?.at),
    atLocal: str(pick(raw, 'atLocal', 'at_local')),
    localDate: str(pick(raw, 'localDate', 'local_date')),
  };
}

/** The axios body of GET /equipment/:id/history. Throws on a body with no row array. */
export function parseEquipmentHistory(body: unknown): EquipmentHistoryResponse {
  const data = (body as any)?.data;
  if (!Array.isArray(data)) {
    throw new Error('Equipment history response carried no row array');
  }
  return {
    timeZone: str(pick(body, 'timeZone', 'time_zone')),
    rows: data.map(parseHistoryRow),
  };
}

/** `lastJob` off an equipment row (list or detail). Null when the asset was never on a job. */
export function parseLastJob(equipmentRow: any): EquipmentLastJob | null {
  const raw = pick(equipmentRow, 'lastJob', 'last_job') as any;
  if (!raw || typeof raw !== 'object') return null;
  const id = str(raw.id);
  if (!id) return null;
  return {
    id,
    jobNumber: str(pick(raw, 'jobNumber', 'job_number')),
    rigName: str(pick(raw, 'rigName', 'rig_name')),
    wellName: str(pick(raw, 'wellName', 'well_name')),
    assignedAt: str(pick(raw, 'assignedAt', 'assigned_at')),
    returnedAt: str(pick(raw, 'returnedAt', 'returned_at')),
  };
}

// ─── Labels ──────────────────────────────────────────────────────────────────

/**
 * Status in plain words: the register's own label (lib/equipment-status), in
 * sentence case so a transition reads as a phrase — "In use → On location".
 * An unknown status is echoed raw by equipmentStatusLabel, never guessed.
 */
export function statusWords(status: string | null): string {
  const label = equipmentStatusLabel(status);
  if (label === DASH) return DASH;
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

/** `job_equipment_assignments.assigned_source`. Unknown values print raw. */
const ASSIGNED_SOURCE_WORDS: Record<string, string> = {
  dispatch: 'dispatch',
  field: 'field',
  transfer: 'transfer',
  ai: 'AI',
};

/**
 * Friendly names for the equipment columns an edit can change. Keys are the
 * database column names the audit log stores. A column missing here prints
 * its raw name — a reader can still tell what changed, and nothing is guessed.
 */
export const EQUIPMENT_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  type: 'Type',
  equipment_type_id: 'Equipment type',
  serial_number: 'Serial number',
  unit_number: 'Unit number',
  manufacturer: 'Manufacturer',
  make: 'Make',
  model: 'Model',
  year: 'Year',
  size: 'Size',
  capacity: 'Capacity',
  status: 'Status',
  status_note: 'Status note',
  notes: 'Notes',
  asset_kind: 'Asset kind',
  license_plate: 'License plate',
  vin: 'VIN',
  photo_url: 'Photo',
  home_yard_id: 'Home shop',
  current_yard_id: 'Current shop',
  current_job_id: 'Current job',
  last_known_rig: 'Last known rig',
  last_known_well: 'Last known well',
  cert_day_limit: 'Cert day limit',
  cert_description: 'Cert level',
  cert_start_date: 'Cert date',
  cert_expiry_date: 'Cert expiry',
  working_days_since_inspection: 'Working days since inspection',
  last_inspection_date: 'Last inspection',
  next_inspection_date: 'Next inspection',
  archived_at: 'Archived',
};

function snake(field: string): string {
  return field.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

export function fieldLabel(field: string): string {
  return EQUIPMENT_FIELD_LABELS[field] ?? EQUIPMENT_FIELD_LABELS[snake(field)] ?? field;
}

/** One side of a before → after. Absent is `—`; a real 0 or false stays. */
export function changeValueText(field: string, value: unknown): string {
  if (value === null || value === undefined) return DASH;
  if (typeof value === 'string' && value.trim() === '') return DASH;
  if (snake(field) === 'status' && typeof value === 'string') return statusWords(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function actorName(actor: EquipmentHistoryActor | null): string {
  if (!actor) return DASH;
  const name = [actor.firstName, actor.lastName].filter(Boolean).join(' ');
  return name || 'Unknown user';
}

export interface HistoryRowView {
  id: string;
  /** `atLocal` exactly as the API sent it, or `—`. */
  when: string;
  action: string;
  /** "from J-1234" on a move between jobs; null otherwise. */
  fromJob: string | null;
  jobId: string | null;
  /** The job number to print; `—` when the row names no job. */
  jobNumber: string;
  rigWell: string;
  who: string;
  /** The assignment's own note or the event's reason, when there is one. */
  note: string | null;
  changes: { label: string; before: string; after: string }[];
}

export function historyActionLabel(row: EquipmentHistoryRow): string {
  switch (row.type) {
    case 'status_change':
      return `${statusWords(row.fromStatus)} → ${statusWords(row.toStatus)}`;
    case 'assigned': {
      const src = row.assignedSource
        ? (ASSIGNED_SOURCE_WORDS[row.assignedSource] ?? row.assignedSource)
        : null;
      return src ? `Signed out (${src})` : 'Signed out';
    }
    case 'returned':
      return 'Returned';
    case 'edited': {
      const labels = (row.changedFields ?? []).map((c) => fieldLabel(c.field));
      return labels.length ? `Edited: ${labels.join(', ')}` : 'Edited';
    }
    case 'deleted':
      return 'Deleted';
    case 'cert_days_changed':
    case 'cert_days_adjusted':
      return 'Cert days changed';
    default:
      // A type this build has never heard of prints raw so somebody notices.
      return row.type || DASH;
  }
}

export function describeHistoryRow(row: EquipmentHistoryRow): HistoryRowView {
  const moved = row.fromJobId && row.fromJobId !== row.jobId;
  const note = [row.reason, row.note].filter(Boolean).join(' · ') || null;
  return {
    id: row.id,
    when: row.atLocal ?? DASH,
    action: historyActionLabel(row),
    fromJob: moved ? `from ${row.fromJobNumber ?? DASH}` : null,
    jobId: row.jobNumber ? row.jobId : null,
    jobNumber: row.jobNumber ?? DASH,
    rigWell: [row.rigName, row.wellName].filter(Boolean).join(' · ') || DASH,
    who: actorName(row.actor),
    note,
    changes: (row.changedFields ?? []).map((c) => ({
      label: fieldLabel(c.field),
      before: changeValueText(c.field, c.before),
      after: changeValueText(c.field, c.after),
    })),
  };
}

export interface LastJobView {
  jobId: string;
  /** "J-1234 · Rig 7 · 100/01-02-…" — job number first, absent parts dropped. */
  summary: string;
  /** "Returned Oct 4, 2026" or "Still out". */
  state: string;
  stillOut: boolean;
}

/**
 * The last-job line. `returnedAt` is an INSTANT, printed as the TENANT's day —
 * never the browser's. A zone that is not known yet prints `—` for the date
 * rather than falling back to the device zone.
 */
export function describeLastJob(lastJob: EquipmentLastJob | null, timeZone: string | null): LastJobView | null {
  if (!lastJob) return null;
  const summary = [lastJob.jobNumber ?? DASH, lastJob.rigName, lastJob.wellName].filter(Boolean).join(' · ');
  if (!lastJob.returnedAt) {
    return { jobId: lastJob.id, summary, state: 'Still out', stillOut: true };
  }
  const day = formatInstantTenantDay(lastJob.returnedAt, timeZone) ?? DASH;
  return { jobId: lastJob.id, summary, state: `Returned ${day}`, stillOut: false };
}
